import React from "react";
import { Skeleton } from "./Skeleton";

/// Shared chrome for hover popovers rendered via `Tooltip variant="card"` — the
/// connection-import schema card and the add-to-dataset card. Centralizing the
/// surface, header, list, rows, and loading skeleton keeps them visually
/// identical (fonts, spacing, borders) instead of drifting as separate copies.

/// Outer card surface + inner text/min-width wrapper. Wrap the card body in this
/// and hand the result to `Tooltip title`.
export const HoverCardSurface: React.FC<{
  children: React.ReactNode;
  maxWidth?: string;
}> = ({ children, maxWidth = "20rem" }) => (
  <div
    className="bg-bg-subtle text-fg border border-border rounded-app shadow-lg select-none cursor-default"
    style={{ maxWidth }}
  >
    <div className="text-fg min-w-55">{children}</div>
  </div>
);

/// Title row. `right` is an optional trailing slot (badges, counts).
export const HoverCardHeader: React.FC<{
  title: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, right }) => (
  <div className="flex items-center gap-1.5 px-2.5 pt-1.5 pb-1">
    <span className="text-fg-regular truncate">{title}</span>
    {right && <span className="ml-auto shrink-0">{right}</span>}
  </div>
);

/// A section label with a leading icon (e.g. "Description", "Tables"), matching
/// the Datasets-page metadata headings: muted icon + muted-medium label, with
/// an optional trailing `meta` slot (e.g. a count chip).
export const HoverCardSectionTitle: React.FC<{
  icon: React.ReactNode;
  label: React.ReactNode;
  meta?: React.ReactNode;
}> = ({ icon, label, meta }) => (
  <div className="flex items-center gap-2 px-2.5 pt-1.5 pb-0.5">
    <span className="shrink-0 inline-flex text-fg-muted">{icon}</span>
    <span className="text-fg-muted-medium truncate">{label}</span>
    {meta}
  </div>
);

/// Scrolling list container for the card body rows.
export const HoverCardList: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ul className="list-none m-0 py-1 max-h-60 overflow-y-auto">{children}</ul>
);

/// A two-column row: primary label (left, flexes + truncates) and a trailing
/// tag (right, hugs). Pass the two cells as children.
export const HoverCardRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <li className="grid grid-cols-[1fr_auto] items-center gap-x-2 px-2.5 py-0.5">
    {children}
  </li>
);

/// Muted single-line message (empty / error / "no items" states).
export const HoverCardNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="px-2.5 py-2 text-fg-muted-regular">{children}</div>;

/// Loading skeleton — one bar per width (percentages).
export const HoverCardSkeleton: React.FC<{ widths?: number[] }> = ({
  widths = [60, 80, 50, 75, 65],
}) => (
  <ul className="list-none m-0 py-1 px-2.5 space-y-1">
    {widths.map((w, i) => (
      <li key={i}>
        <Skeleton width={`${w}%`} />
      </li>
    ))}
  </ul>
);
